"use client";

import React, { useState, useCallback, useMemo } from "react";
import { Button, Checkbox, Dropdown, Input, Tabs } from "antd";
import type { TabsProps } from "antd";
import { DownOutlined, CloseCircleFilled } from "@ant-design/icons";
import { formatMsg, Helper } from "../..";

interface Person {
    id: string;
    name: string;
    type: "Account" | "Staff" | "User";
    role?: string;
}

interface Props {
    peopleIdToObjMap: Map<string, Person>;
    peopleFilter: string[];
    setPeopleFilter: (ids: string[]) => void;
    disabled?: boolean;
    firebase: any;
}

/**
 * People filter dropdown – now using antd `Tabs` with one **shared** search box.
 */
const PeopleFilterDropdown: React.FC<Props> = ({
    peopleIdToObjMap,
    peopleFilter,
    setPeopleFilter,
    disabled,
    firebase
}) => {
    /** -------------------------------------------------- State */
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState<string>("all");

    /** -------------------------------------------------- Helpers */
    // Group people by category (memoised for performance)
    const options = useMemo(() => {
        const all = Array.from(peopleIdToObjMap.values());
        return {
            all,
            accounts: all.filter((p) => p.type === "Account"),
            instructors: all.filter((p) => p.type === "Staff"),
            participants: all.filter((p) => p.type === "User"),
        } as const;
    }, [peopleIdToObjMap]);

    /** Persist state + URL*/
    const commitFilter = useCallback(
        (ids: string[]) => {
            const unique = Array.from(new Set(ids));
            setPeopleFilter(unique);
            Helper.setUrlParam(
                "timelinePeopleFilter",
                unique.length ? JSON.stringify(unique) : ""
            );
        },
        [setPeopleFilter]
    );

    /** Toggle an entire category */
    const handleCategoryToggle = useCallback(
        (category: keyof typeof options) => {
            const catIds = options[category].map((p) => p.id);
            const isEverySelected = catIds.every((id) => peopleFilter.includes(id));
            const next = isEverySelected
                ? peopleFilter.filter((id) => !catIds.includes(id))
                : Array.from(new Set([...peopleFilter, ...catIds]));
            commitFilter(next);
        },
        [options, peopleFilter, commitFilter]
    );

    /** Toggle a single person */
    const handlePersonToggle = useCallback(
        (id: string, checked: boolean) => {
            const next = checked
                ? [...peopleFilter, id]
                : peopleFilter.filter((pid) => pid !== id);
            commitFilter(next);
        },
        [peopleFilter, commitFilter]
    );

    /** Render list inside each tab */
    const renderPersonList = (category: keyof typeof options) => {
        const visibleOptions = options[category].filter((p) =>
            p.name.toLowerCase().includes(searchQuery.toLowerCase())
        );

        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {visibleOptions.map((option) => (
                    <div key={option.id} className="custom-dropdown-option-menu-container">
                        <Checkbox
                            checked={peopleFilter.includes(option.id)}
                            onChange={(e) =>
                                handlePersonToggle(option.id, e.target.checked)
                            }
                        >
                            <div style={styles.customDropdownItem}>
                                <img
                                    src={Helper.getProfileImageUrl(option)}
                                    style={styles.customDropdownLogo}
                                    alt="profile"
                                />
                                <div style={styles.customDropdownSubitem}>
                                    <div style={styles.customDropdownLabel}>{option.name}</div>
                                    <div style={styles.customDropdownDescription}>
                                        {option.type === "User" ? Helper.getSelectedUserTypeName(firebase, option.type) : (option.role || option.type)}
                                    </div>
                                </div>
                            </div>
                        </Checkbox>
                    </div>
                ))}
            </div>
        );
    };

    /** Build tab items */
    const tabItems: TabsProps["items"] = (
        Object.keys(options) as Array<keyof typeof options>
    ).map((key) => {
        const ids = options[key].map((p) => p.id);
        const total = ids.length;
        const selected = ids.filter((id) => peopleFilter.includes(id)).length;

        return {
            key,
            label: (
                <div>
                    <Checkbox
                        checked={total > 0 && selected === total}
                        indeterminate={selected > 0 && selected < total}
                        onChange={() => handleCategoryToggle(key)}
                    >
                    </Checkbox>
                    <span style={{ textTransform: "capitalize", marginLeft: 10 }}>
                        {key === "participants" ?
                            Helper.getSelectedUserTypeName(firebase, formatMsg(key), undefined, undefined) :
                            formatMsg(key)
                        }
                    </span>
                </div>
            ),
            children: renderPersonList(key),
        };
    });

    /** Clear all selected people */
    const handleClearSelection = (e) => {
        e.preventDefault()
        e.stopPropagation()
        commitFilter([]);
    };

    /** -------------------------------------------------- Render */
    const selectedCount = peopleFilter.length;

    return (
        <div className="custom-dropdown-container">
            <Dropdown
                disabled={disabled}
                destroyPopupOnHide
                trigger={["click"]}
                dropdownRender={() => (
                    <div style={{ background: "#fff", padding: 16, boxShadow: "0px 4px 10px 0px #00000033", borderRadius: 4 }}>
                        {/* Shared search box */}
                        <Input
                            placeholder="Search people…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="custom-dropdown-search"
                            style={{ marginBottom: 16 }}
                        />

                        {/* Category tabs */}
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={tabItems}
                            style={{ maxHeight: 400, overflowY: "auto" }}
                        />
                    </div>
                )}
            >
                <Button style={{ width: "100%" }}>
                    <div
                        className="custom-dropdown-btn"
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            color: disabled ? "#00000040" : "rgba(0,0,0,0.65)",
                        }}
                    >
                        {selectedCount > 0
                            ? `${selectedCount} People`
                            : "People"}
                        {selectedCount > 0 ? (
                            <CloseCircleFilled onClick={handleClearSelection} style={{ fontSize: 10, color: "#645c5ca6" }} />
                        ) : (
                            <DownOutlined style={{ fontSize: 12 }} />
                        )}
                    </div>
                </Button>
            </Dropdown>
        </div>
    );
};

export default PeopleFilterDropdown;

/* ----------------------- Inline styles ------------------------ */
const styles: { [key: string]: React.CSSProperties } = {
    customDropdownItem: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        gap: 10,
        alignItems: "center",
        width: "100%",
    },
    customDropdownSubitem: {
        display: "flex",
        flexDirection: "column",
        gap: 5,
    },
    customDropdownLogo: {
        borderRadius: "50%",
        width: 35,
        height: 35,
        objectFit: "cover",
    },
    customDropdownLabel: {
        fontSize: 15,
        fontWeight: 500,
        color: "#000",
    },
    customDropdownDescription: {
        color: "#8d8d8d",
        fontWeight: 400,
        fontSize: 12,
    },
};
